/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Section from "model/abilities/Section";
import SkillStats from "model/stats/SkillStats";

interface TableRow {
  name: string;
  desc: string;
}

class CharacterService {

  static getTableRow = (tableText: string, rowKey: string): TableRow[] | null => {
    const rows: string[] = tableText.split('\n');
    const headers: string[] = rows[0].split('|');
    for (const row of rows) {
      const data = row.split('|');
      if (data.length > 1 && data[1].trim().toLowerCase() === rowKey.trim().toLowerCase()) {
        const results: TableRow[] = [];
        let spellSlots: string[] | null = null;
        for (let i=0 ; i<headers.length; i++) {
          const name = headers[i].trim();
          const desc = data[i].trim();
          if (i > 1 && name && desc && desc !== '-') {
            if (name === 'Known Spells') {
              spellSlots = [];
            } else if (spellSlots !== null && ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th'].includes(name)) {
              spellSlots.push(`**${name}**: ${desc}`);
            }
            results.push({ name, desc });
          }
        }
        if (spellSlots !== null) {
          results.push({ name: 'Spell Slots', desc: spellSlots.join(', ') });
        }
        return results;
      }
    }
    return null;
  }

  static splitItems = (text: string | null, itemsArray: string[]): string[] => {
    /*
        "Choose any three",
        "Choose one from <?>, <?>, <?>, <?>, and <?>",
        "Choose two from <?>, <?>, <?>, <?>, and <?>",
        "Choose two from <?>, <?>, <?>, <?>, <?>, and <?>",
        "Choose two from <?>, <?>, <?>, <?>, <?>, <?>, <?>, and <?>",
        "Choose two skills from <?>, <?>, <?>, <?>, <?>, <?>, <?>, and <?>",
        "Choose two skills from <?>, <?>, <?>, <?>, <?>, <?>, and <?>"
        "Choose three from <?>, <?>, <?>, <?>, <?>, <?>, <?>, and <?>",
        "Choose four from <?>, <?>, <?>, <?>, <?>, <?>, <?>, <?>, <?>, <?>, and <?>",
        "<?>, <?>",
        "<?> and <?>.",
        "<?>, and either <?> or <?>.",
        "<?>, and either <?>, <?>, or <?>.",
        "<?>, and either <?>, <?>, <?>, or <?>.",
        "<?>, plus your choice of one between <?> or <?>.",
        "<?> plus one of your choice from among <?> or <?>",
        "Two of your choice.",
        "Your choice of two from among <?>, <?>, and <?>.",
    */
    if (text == null) {
      return itemsArray;
    }
    const phrasesOrFour = text.split(/(your choice of|choose) four( skills)? from( among)? /i);
    const phrasesOrThree = text.split(/(your choice of|choose) three( skills)? from( among)? /i);
    const phrasesOrTwo = text.split(/(your choice of|choose) two( skills)? from( among)? /i);
    const phrasesOrOne = text.split(/ and either | (plus )?your choice of one (from|between) | plus one of your choice from among /i);
    if (phrasesOrFour.length > 1) {
      text = phrasesOrFour[0].trim();
      itemsArray.push('either ' + phrasesOrFour[phrasesOrFour.length - 1].trim());
      itemsArray.push('either ' + phrasesOrFour[phrasesOrFour.length - 1].trim());
      itemsArray.push('either ' + phrasesOrFour[phrasesOrFour.length - 1].trim());
      itemsArray.push('either ' + phrasesOrFour[phrasesOrFour.length - 1].trim());
    } else if (phrasesOrThree.length > 1) {
      text = phrasesOrThree[0].trim();
      itemsArray.push('either ' + phrasesOrThree[phrasesOrThree.length - 1].trim());
      itemsArray.push('either ' + phrasesOrThree[phrasesOrThree.length - 1].trim());
      itemsArray.push('either ' + phrasesOrThree[phrasesOrThree.length - 1].trim());
    } else if (phrasesOrTwo.length > 1) {
      text = phrasesOrTwo[0].trim();
      itemsArray.push('either ' + phrasesOrTwo[phrasesOrTwo.length - 1].trim());
      itemsArray.push('either ' + phrasesOrTwo[phrasesOrTwo.length - 1].trim());
    } else if (phrasesOrOne.length > 1) {
      text = phrasesOrOne[0].trim();
      itemsArray.push('either ' + phrasesOrOne[phrasesOrOne.length - 1].trim());
    }

    for (const word of text.split(',')) {
      if (word === 'Choose any three' || word === 'Three of your choice.') {
        itemsArray.push('any');
        itemsArray.push('any');
        itemsArray.push('any');
      } else if (word === 'Choose any two' || word === 'Two of your choice.') {
        itemsArray.push('any');
        itemsArray.push('any');
      } else if (word === 'Choose any') {
        itemsArray.push('any');
      } else {
        itemsArray.push(word.trim());
      }
    }
    return itemsArray.reverse();
  }

  static splitSkills = (text: string, name: string, skillOptions: { label: string, options: string[] }[], skillsSelected: string[]): void => {
    text = text?.replace('Animal, Handling', 'Animal Handling') || ''; // bug fix

    const proficientSkills = CharacterService.splitItems(text, []);
    for (const prof of proficientSkills) {
      if (prof === 'any') {
        skillOptions.push({ label: name, options: [...SkillStats.getSkillsMap().keys()] });
        skillsSelected.push('');
        continue;
      }
      const prof2 = prof.toLowerCase()
        .replace(/,? and /gi, ',')
        .replace(/,? or /gi, ',')
        .replace(/, /g, ',')
        .replace(/\./g, '');

      if (prof.startsWith('either ')) {
        const options = prof2.replace(/^either /i, '')
          .split(',');
        const validOptions: string[] = [];
        for (const option of options) {
          for (const skill of SkillStats.getSkillsMap().keys()) {
            if (option.trim() === skill.toLowerCase()) {
              validOptions.push(skill);
              break;
            }
          }
        }
        if (validOptions.length) {
          skillOptions.push({ label: name, options: validOptions });
          skillsSelected.push('');
        }
        continue;
      }
      for (const option of prof2.split(',')) {
        for (const skill of SkillStats.getSkillsMap().keys()) {
          if (option.trim() === skill.toLowerCase()) {
            skillOptions.push({ label: name, options: [skill] });
            skillsSelected.push(skill);
            break;
          }
        }
      }
    }
  }
}

export default CharacterService;
        